/* eslint-disable react/prop-types */
import type { Placement, StrictModifiers } from '@popperjs/core';
import React, {
    MutableRefObject,
    ReactNode,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { DefaultTheme } from 'styled-components';
import { StyledArrow, StyledTooltip } from './Tooltip.styles';
export interface TooltipRefProps {
    api: {
        open: () => void;
        close: () => void;
        toggle: () => void;
    };
}

export interface TooltipProps {
    placement?: Placement;
    target: MutableRefObject<any> | HTMLElement | string;
    children: ReactNode;
    autoHide?: boolean;
    popperModifiers?: StrictModifiers[];
    theme?: DefaultTheme;
    popperOptions?: {
        strategy?: 'absolute' | 'fixed';
    };
    usePortal?: boolean;
}

const ToolTip = forwardRef<TooltipRefProps, TooltipProps>(
    (
        {
            placement = 'auto',
            children,
            target,
            autoHide = true,
            popperModifiers,
            theme,
            popperOptions = {
                strategy: 'absolute'
            },
            usePortal,
            ...rest
        },
        ref
    ) => {
        const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
        const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
        const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
        const [open, setOpen] = useState(false);
        const [mounted, setMounted] = useState(false);
        const [isOpening, setIsOpening] = useState(false);

        const tooltipHovered = useRef(false);

        const getPopperModifiers = () => {
            return popperModifiers && popperModifiers.length > 0 ? popperModifiers : [];
        };
        const { styles, attributes } = usePopper(referenceElement, popperElement, {
            placement,
            ...popperOptions,
            modifiers: [
                { name: 'offset', options: { offset: [0, 8] } },
                {
                    name: 'arrow',
                    options: {
                        element: arrowElement
                    }
                },
                ...getPopperModifiers()
            ]
        });

        const handleMouseEnterPopover = () => {
            if (autoHide) return;
            tooltipHovered.current = true;
        };

        const handleMouseLeavePopover = () => {
            if (autoHide) return;
            setIsOpening(false);
            tooltipHovered.current = false;
            setTimeout(() => {
                setOpen(false);
            }, 200);
        };

        const handleMouseEnter = () => {
            setOpen(true);
            setIsOpening(true);
        };

        const handleMouseLeave = () => {
            setTimeout(() => {
                if (!tooltipHovered.current) {
                    setIsOpening(false);
                    setTimeout(() => {
                        setOpen(false);
                    }, 200);
                }
            }, 100);
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpen(false);
            }
        };

        useEffect(() => {
            if (!target || (typeof target !== 'object' && typeof target !== 'string')) return;

            const targetAsMutableRef = target as MutableRefObject<any>;
            const targetElement: any =
                typeof target === 'object'
                    ? targetAsMutableRef.current
                        ? (target as MutableRefObject<any>)?.current
                        : target
                    : (document.getElementById(target as string) as HTMLElement);

            if (!targetElement) return;
            setReferenceElement(targetElement);

            if (!mounted) return;
            targetElement.addEventListener('mouseenter', handleMouseEnter);
            targetElement.addEventListener('mouseleave', handleMouseLeave);
            targetElement.addEventListener('focus', handleMouseEnter);
            targetElement.addEventListener('blur', handleMouseLeave);
            targetElement.addEventListener('keydown', handleKeyDown);
            return () => {
                targetElement.removeEventListener('mouseenter', handleMouseEnter);
                targetElement.removeEventListener('mouseleave', handleMouseLeave);
                targetElement.removeEventListener('focus', handleMouseEnter);
                targetElement.removeEventListener('blur', handleMouseLeave);
                targetElement.removeEventListener('keydown', handleKeyDown);
            };
        }, [target, mounted]);

        useEffect(() => {
            setMounted(true);
            return () => {
                setMounted(false);
            };
        }, []);

        useImperativeHandle(ref, () => ({
            api: {
                open: () => {
                    setOpen(true);
                },
                close: () => {
                    setOpen(false);
                },
                toggle: () => {
                    setOpen(!open);
                }
            }
        }));

        const tooltipElement = (
            <StyledTooltip
                ref={setPopperElement}
                style={styles.popper}
                $isOpening={isOpening}
                onMouseEnter={() => handleMouseEnterPopover()}
                onMouseLeave={() => handleMouseLeavePopover()}
                theme={theme}
                role='tooltip'
                {...attributes.popper}
                {...rest}
            >
                {children}
                <StyledArrow ref={setArrowElement} style={styles.arrow} />
            </StyledTooltip>
        );
        return <>{isOpening && (usePortal ? createPortal(tooltipElement, document.body) : tooltipElement)}</>;
    }
);

ToolTip.displayName = 'Tooltip';
export default ToolTip;
